import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isValid,
  parseISO,
} from 'date-fns'
import { Modal, Button, Select, DateInput } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { useCreateLeave, useUpdateLeave } from '@/features/leave/hooks/useLeaves'
import { useLeaveTypes } from '@/features/leave/hooks/useLeaveTypes'
import {
  createLeaveRequestSchema,
  type CreateLeaveRequestFormData,
} from '@/features/leave/schemas/leaveRequest.schema'
import type { LeaveDocument } from '@/features/leave/types/leave.types'

const DURATION_OPTIONS: SelectOption[] = [
  { value: 'full', label: 'Full day' },
  { value: 'half', label: 'Half day' },
]

const todayISO = format(new Date(), 'yyyy-MM-dd')

const parseValidISO = (value?: string): Date | null => {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/** Count working days (Mon–Fri) in an inclusive date range. */
const countWorkingDays = (start: Date, end: Date): number =>
  eachDayOfInterval({ start, end }).filter((day) => {
    const weekday = day.getDay()
    return weekday !== 0 && weekday !== 6
  }).length

interface CreateLeaveModalProps {
  companyId: string
  open: boolean
  onClose: () => void
  leave?: LeaveDocument | null
}

export function CreateLeaveModal({
  companyId,
  open,
  onClose,
  leave,
}: CreateLeaveModalProps): React.ReactElement | null {
  const createMutation = useCreateLeave()
  const updateMutation = useUpdateLeave()
  const isEditing = !!leave
  const isPending = createMutation.isPending || updateMutation.isPending

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateLeaveRequestFormData>({
    resolver: zodResolver(createLeaveRequestSchema),
    defaultValues: { durationType: 'full' },
  })

  const leaveTypesQuery = useLeaveTypes({ enabled: open })
  const currentLeaveType = leave?.leaveType
  // Options come from the organization's leave types; an edited request keeps
  // its current type even if that type has since been switched off.
  const leaveTypeOptions = useMemo<SelectOption[]>(() => {
    const options = (leaveTypesQuery.data ?? []).map((type) => ({
      value: type.key,
      label: type.isPaid ? type.label : `${type.label} (unpaid)`,
    }))
    if (currentLeaveType && !options.some((option) => option.value === currentLeaveType)) {
      options.push({ value: currentLeaveType, label: currentLeaveType })
    }
    return options
  }, [leaveTypesQuery.data, currentLeaveType])

  const durationType = watch('durationType')
  const startDate = watch('startDate')
  const endDate = watch('endDate')

  const startDateObj = parseValidISO(startDate)
  const endDateObj = parseValidISO(endDate)
  const isMultiDay =
    !!startDateObj && !!endDateObj && differenceInCalendarDays(endDateObj, startDateObj) > 0

  // Half day only makes sense on a single date; force back to full for a range.
  useEffect(() => {
    if (isMultiDay && durationType === 'half') {
      setValue('durationType', 'full', { shouldValidate: true })
    }
  }, [isMultiDay, durationType, setValue])

  const durationSummary = useMemo(() => {
    if (!startDateObj || !endDateObj) return null
    if (differenceInCalendarDays(endDateObj, startDateObj) < 0) return null

    const rangeLabel =
      differenceInCalendarDays(endDateObj, startDateObj) === 0
        ? format(startDateObj, 'EEE d MMM yyyy')
        : `${format(startDateObj, 'EEE d MMM')} – ${format(endDateObj, 'EEE d MMM yyyy')}`

    if (durationType === 'half') {
      return { count: '½ working day', rangeLabel }
    }

    const workingDays = countWorkingDays(startDateObj, endDateObj)
    return {
      count: `${workingDays} working day${workingDays === 1 ? '' : 's'}`,
      rangeLabel,
    }
  }, [startDateObj, endDateObj, durationType])

  // A half day only applies to a single date, so hide it for multi-day ranges.
  const durationOptions = isMultiDay
    ? DURATION_OPTIONS.filter((option) => option.value !== 'half')
    : DURATION_OPTIONS

  useEffect(() => {
    if (!open) return
    reset(
      leave
        ? {
            leaveType: leave.leaveType,
            startDate: leave.startDate.slice(0, 10),
            endDate: leave.endDate.slice(0, 10),
            reason: leave.reason ?? '',
            durationType: leave.durationType,
          }
        : { durationType: 'full' }
    )
  }, [leave, open, reset])

  const onSubmit = (data: CreateLeaveRequestFormData) => {
    if (leave) {
      updateMutation.mutate(
        { companyId, leaveId: leave._id, body: data },
        {
          onSuccess: () => {
            reset()
            onClose()
          },
        }
      )
      return
    }
    createMutation.mutate(
      { companyId, body: data },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      }
    )
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEditing ? 'Edit leave request' : 'Request leave'}
      className="min-h-[min(85dvh,42rem)] max-w-xl"
      footer={
        <div className="flex gap-2">
          <Button type="submit" form="create-leave-form" variant="primary" loading={isPending}>
            {isPending
              ? isEditing
                ? 'Saving…'
                : 'Submitting…'
              : isEditing
                ? 'Save changes'
                : 'Submit request'}
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="create-leave-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="leaveType"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Leave type <span className="text-red-500">*</span>
          </label>
          <Select
            id="leaveType"
            options={leaveTypeOptions}
            value={watch('leaveType') ?? ''}
            onChange={(v) => setValue('leaveType', v, { shouldValidate: true })}
            placeholder="Select type"
          />
          {errors.leaveType && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.leaveType.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="startDate"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Start date <span className="text-red-500">*</span>
            </label>
            <DateInput
              id="startDate"
              min={todayISO}
              error={errors.startDate?.message}
              {...register('startDate', {
                onChange: (e) => {
                  const next = e.target.value
                  // Auto-fill end date on first pick, or nudge it forward if now before start.
                  if (next && (!endDate || endDate < next)) {
                    setValue('endDate', next, { shouldValidate: true })
                  }
                },
              })}
            />
          </div>
          <div>
            <label
              htmlFor="endDate"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              End date <span className="text-red-500">*</span>
            </label>
            <DateInput
              id="endDate"
              min={startDate || todayISO}
              error={errors.endDate?.message}
              {...register('endDate')}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="durationType"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Duration
          </label>
          <Select
            id="durationType"
            options={durationOptions}
            value={durationType ?? 'full'}
            onChange={(v) =>
              setValue('durationType', v as 'full' | 'half', { shouldValidate: true })
            }
          />
          {isMultiDay && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Half day is only available for a single-day request.
            </p>
          )}
        </div>

        {durationSummary && (
          <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 dark:border-primary/30 dark:bg-primary/10">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {durationSummary.count}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {durationSummary.rangeLabel}
            </span>
          </div>
        )}

        <div>
          <label
            htmlFor="reason"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason
          </label>
          <textarea
            id="reason"
            {...register('reason')}
            rows={3}
            placeholder="Optional reason for the leave request"
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </form>
    </Modal>
  )
}

CreateLeaveModal.displayName = 'CreateLeaveModal'
